import { useState, useEffect } from 'react'
import { Cloud, Sun, CloudRain, CloudSnow, CloudDrizzle, Wind, Droplets, Thermometer, CloudLightning } from 'lucide-react'
import { useDhamLang } from '../../context/ManaunaDhamLangContext'

interface DayForecast {
  day: string
  high: number
  low: number
  code: number
}

interface CurrentWeather {
  temp: number
  wind: number
  humidity: number
  code: number
}

const LAT = 28.271
const LON = 79.140

function weatherIcon(code: number, size = 'h-8 w-8') {
  const cls = `${size} drop-shadow-sm`
  if (code === 0 || code === 1) return <Sun className={`${cls} text-amber-400`} />
  if (code === 2) return <Cloud className={`${cls} text-gray-400`} />
  if (code === 3) return <Cloud className={`${cls} text-gray-500`} />
  if (code >= 51 && code <= 57) return <CloudDrizzle className={`${cls} text-blue-400`} />
  if (code >= 61 && code <= 67) return <CloudRain className={`${cls} text-blue-500`} />
  if (code >= 71 && code <= 77) return <CloudSnow className={`${cls} text-blue-200`} />
  if (code >= 80 && code <= 82) return <CloudRain className={`${cls} text-blue-600`} />
  if (code >= 95) return <CloudLightning className={`${cls} text-yellow-500`} />
  return <Cloud className={`${cls} text-gray-400`} />
}

function weatherLabel(code: number): string {
  if (code === 0) return 'Clear'
  if (code === 1) return 'Mostly clear'
  if (code === 2) return 'Partly cloudy'
  if (code === 3) return 'Overcast'
  if (code >= 51 && code <= 57) return 'Drizzle'
  if (code >= 61 && code <= 67) return 'Rain'
  if (code >= 71 && code <= 77) return 'Snow'
  if (code >= 80 && code <= 82) return 'Showers'
  if (code >= 95) return 'Thunderstorm'
  return 'Cloudy'
}

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export default function WeatherWidget() {
  const { t } = useDhamLang()
  const [forecast, setForecast] = useState<DayForecast[]>([])
  const [current, setCurrent] = useState<CurrentWeather | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min&timezone=Asia/Kolkata&forecast_days=5`

    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        if (data.current) {
          setCurrent({
            temp: Math.round(data.current.temperature_2m * 10) / 10,
            wind: Math.round(data.current.wind_speed_10m * 10) / 10,
            humidity: data.current.relative_humidity_2m,
            code: data.current.weather_code,
          })
        }
        if (data.daily) {
          const days: DayForecast[] = data.daily.time.map((d: string, i: number) => ({
            day: DAYS_SHORT[new Date(d).getDay()],
            high: Math.round(data.daily.temperature_2m_max[i] * 10) / 10,
            low: Math.round(data.daily.temperature_2m_min[i] * 10) / 10,
            code: data.daily.weather_code[i],
          }))
          setForecast(days)
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-amber-50 border border-secondary-200 p-8 animate-pulse">
        <div className="h-6 w-48 bg-secondary-200 rounded mb-4" />
        <div className="grid grid-cols-5 gap-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-white/60 rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  if (!current && forecast.length === 0) return null

  return (
    <div className="rounded-2xl bg-gradient-to-br from-blue-50 via-amber-50/50 to-orange-50 border border-secondary-200 overflow-hidden shadow-sm">
      <div className="px-6 pt-6 pb-4">
        <h3 className="text-xl font-bold text-secondary-900">
          {t({
            en: 'Manauna Dham Weather',
            hi: 'मनौना धाम मौसम',
            hinglish: 'Manauna Dham Ka Mausam',
          })}
        </h3>
        <p className="text-sm text-secondary-500 mt-1">
          {t({
            en: 'Bareilly region · Live forecast',
            hi: 'बरेली क्षेत्र · लाइव पूर्वानुमान',
            hinglish: 'Bareilly region · Live forecast',
          })}
        </p>
      </div>

      {current && (
        <div className="px-6 pb-4">
          <div className="flex items-center gap-6 p-5 rounded-2xl bg-white/80 backdrop-blur-sm border border-white/50 shadow-sm">
            <div className="relative">
              <div className="absolute inset-0 bg-amber-200/30 rounded-full blur-xl animate-pulse" />
              {weatherIcon(current.code, 'h-16 w-16')}
            </div>
            <div>
              <div className="text-4xl font-bold text-secondary-900">
                {current.temp}°C
              </div>
              <div className="text-sm text-secondary-600 font-medium mt-1">
                {weatherLabel(current.code)}
              </div>
            </div>
            <div className="ml-auto grid grid-cols-1 gap-2 text-sm">
              <div className="flex items-center gap-2 text-secondary-600">
                <Wind className="h-4 w-4 text-blue-500" />
                <span>{current.wind} km/h</span>
              </div>
              <div className="flex items-center gap-2 text-secondary-600">
                <Droplets className="h-4 w-4 text-blue-400" />
                <span>{current.humidity}%</span>
              </div>
              <div className="flex items-center gap-2 text-secondary-600">
                <Thermometer className="h-4 w-4 text-red-400" />
                <span>{t({ en: 'Feels warm', hi: 'गर्म', hinglish: 'Warm hai' })}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {forecast.length > 0 && (
        <div className="px-6 pb-6">
          <div className="grid grid-cols-5 gap-2">
            {forecast.map((d, i) => (
              <div
                key={d.day + i}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all duration-300
                  ${i === 0 ? 'bg-primary-100/80 border border-primary-300/50 shadow-sm' : 'bg-white/60 border border-white/50 hover:bg-white/80'}`}
              >
                <span className={`text-xs font-bold ${i === 0 ? 'text-primary-800' : 'text-secondary-600'}`}>
                  {i === 0 ? t({ en: 'Today', hi: 'आज', hinglish: 'Aaj' }) : d.day}
                </span>
                <div className="my-1">{weatherIcon(d.code, 'h-7 w-7')}</div>
                <div className="text-xs font-semibold text-secondary-800">
                  {d.high}°
                </div>
                <div className="text-xs text-secondary-500">
                  {d.low}°
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
